import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';

import { ChatSidebar } from '@/components/mensajes/ChatSidebar';
import { ChatWindow } from '@/components/mensajes/ChatWindow';
import { NewChatDialog } from '@/components/mensajes/NewChatDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('http', 'ws') || 'ws://localhost:8001';

const Mensajes = () => {
  const [conversaciones, setConversaciones] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [selectedConversacion, setSelectedConversacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatData, setNewChatData] = useState({ telefono: '', nombreContacto: '', mensaje: '' });
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [llmLoading, setLlmLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageSkip, setPageSkip] = useState(0);
  
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const hasLoadedOlderRef = useRef(false);
  
  const MESSAGES_PER_PAGE = 50;
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const selectedConversacionRef = useRef(null);

  // Keep ref in sync with state for WebSocket closure access
  useEffect(() => {
    selectedConversacionRef.current = selectedConversacion;
  }, [selectedConversacion]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const connectWebSocket = () => {
      if (wsRef.current) wsRef.current.close();

      const ws = new WebSocket(`${WS_URL}/api/whatsapp/ws`);

      ws.onopen = () => {
        console.log('WebSocket conectado');
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            const newMsg = data.mensaje;
            const currentConv = selectedConversacionRef.current;
            if (currentConv && currentConv.id === data.conversation_id) {
              setMensajes(prev => {
                if (prev.some(m => m.id === newMsg.id)) return prev;
                return [...prev, newMsg];
              });
            }
            setConversaciones(prev => {
              const existing = prev.find(c => c.id === data.conversation_id);
              if (existing) {
                const isViewingThis = currentConv && currentConv.id === data.conversation_id;
                return prev.map(c =>
                  c.id === data.conversation_id
                    ? {
                      ...c,
                      ultimoMensaje: newMsg.mensaje,
                      ultimaFecha: newMsg.createdAt,
                      mensajesNoLeidos: isViewingThis ? 0 : (c.mensajesNoLeidos || 0) + 1
                    }
                    : c
                );
              } else {
                loadConversaciones();
                return prev;
              }
            });
          } else if (data.type === 'status_update') {
            setMensajes(prev => prev.map(m =>
              m.id === data.message_id ? { ...m, estado: data.status, error_code: data.error_code } : m
            ));
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket desconectado');
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadConversaciones();
    const checkMobile = () => setIsMobileView(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (selectedConversacion) {
      loadMensajes(selectedConversacion.id);
      loadLlmSettings(selectedConversacion.telefono);
    }
  }, [selectedConversacion]);

  useEffect(() => {
    if (hasLoadedOlderRef.current) {
      hasLoadedOlderRef.current = false;
      return;
    }
    scrollToBottom();
  }, [mensajes]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView();
  };

  const loadConversaciones = async () => {
    try {
      const response = await axios.get(`${API}/conversaciones`);
      setConversaciones(response.data);
    } catch (error) {
      console.error('Error loading conversaciones:', error);
      toast.error('Error al cargar conversaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadMensajes = async (conversacionId) => {
    try {
      const response = await axios.get(`${API}/conversaciones/${conversacionId}/mensajes`, {
        params: { skip: 0, limit: MESSAGES_PER_PAGE }
      });
      setMensajes(response.data);
      setPageSkip(0);
      setHasMore(response.data.length === MESSAGES_PER_PAGE);
      hasLoadedOlderRef.current = false;
      setConversaciones(prev => prev.map(c =>
        c.id === conversacionId ? { ...c, mensajesNoLeidos: 0 } : c
      ));
    } catch (error) {
      console.error('Error loading mensajes:', error);
      toast.error('Error al cargar mensajes');
    }
  };

  const loadMoreMensajes = async () => {
    if (loadingMoreRef.current || !hasMore || !selectedConversacion) return;
    
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const scrollContainer = scrollContainerRef.current;
    const oldScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
    const nextSkip = pageSkip + MESSAGES_PER_PAGE;
    
    try {
      const response = await axios.get(`${API}/conversaciones/${selectedConversacion.id}/mensajes`, {
        params: { skip: nextSkip, limit: MESSAGES_PER_PAGE }
      });
      
      const newMensajes = response.data;
      if (newMensajes.length < MESSAGES_PER_PAGE) {
        setHasMore(false);
      }
      
      if (newMensajes.length > 0) {
        hasLoadedOlderRef.current = true;
        setMensajes(prev => [...newMensajes, ...prev]);
        setPageSkip(nextSkip);
        
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            scrollContainerRef.current.scrollTop = newScrollHeight - oldScrollHeight;
          }
        });
      }
    } catch (error) {
      console.error('Error loading more mensajes:', error);
      toast.error('Error al cargar más mensajes');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    if (e.target.scrollTop <= 10) {
      loadMoreMensajes();
    }
  };

  const loadLlmSettings = async (telefono) => {
    try {
      const response = await axios.get(`${API}/conversaciones/${telefono}/llm-settings`);
      setLlmEnabled(response.data.llm_enabled !== false);
    } catch (error) {
      console.error('Error loading LLM settings:', error);
      setLlmEnabled(true);
    }
  };

  const toggleLlmEnabled = async () => {
    if (!selectedConversacion) return;
    setLlmLoading(true);
    const newValue = !llmEnabled;
    try {
      await axios.put(`${API}/conversaciones/${selectedConversacion.telefono}/llm-settings`, {
        llm_enabled: newValue
      });
      setLlmEnabled(newValue);
      toast.success(newValue
        ? 'Respuesta automática activada'
        : 'Respuesta automática desactivada - puedes responder manualmente'
      );
    } catch (error) {
      console.error('Error updating LLM settings:', error);
      toast.error('Error al actualizar configuración');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!nuevoMensaje.trim() || !selectedConversacion) return;

    const tempId = `temp-${Date.now()}`;
    const mensajeTexto = nuevoMensaje;

    const mensajeOptimista = {
      id: tempId,
      conversacionId: selectedConversacion.id,
      telefono: selectedConversacion.telefono,
      nombreContacto: selectedConversacion.nombreContacto,
      mensaje: mensajeTexto,
      direccion: 'saliente',
      estado: 'enviando',
      createdAt: new Date().toISOString()
    };

    setMensajes(prev => [...prev, mensajeOptimista]);
    setNuevoMensaje('');

    try {
      const response = await axios.post(`${API}/mensajes`, {
        telefono: selectedConversacion.telefono,
        nombreContacto: selectedConversacion.nombreContacto,
        mensaje: mensajeTexto,
        direccion: 'saliente',
        loteId: selectedConversacion.loteId,
        clienteId: selectedConversacion.clienteId
      });

      setMensajes(prev => prev.map(m =>
        m.id === tempId ? response.data : m
      ));

      setConversaciones(prev => prev.map(c =>
        c.id === selectedConversacion.id
          ? { ...c, ultimoMensaje: mensajeTexto, ultimaFecha: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      setMensajes(prev => prev.map(m =>
        m.id === tempId ? { ...m, estado: 'fallido' } : m
      ));
      toast.error('Error al enviar mensaje');
    }
  };

  const handleNewChat = async () => {
    if (!newChatData.telefono || !newChatData.nombreContacto || !newChatData.mensaje) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      await axios.post(`${API}/mensajes`, {
        telefono: newChatData.telefono,
        nombreContacto: newChatData.nombreContacto,
        mensaje: newChatData.mensaje,
        direccion: 'saliente'
      });

      toast.success('Conversación iniciada');
      setShowNewChat(false);
      setNewChatData({ telefono: '', nombreContacto: '', mensaje: '' });
      loadConversaciones();

      const telefonoNorm = newChatData.telefono.replace(/\s|-/g, '');
      setSelectedConversacion({
        id: `conv-${telefonoNorm}`,
        telefono: newChatData.telefono,
        nombreContacto: newChatData.nombreContacto
      });
      if (isMobileView) setShowChatOnMobile(true);
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Error al iniciar conversación');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  const totalNoLeidos = conversaciones.reduce((acc, c) => acc + (c.mensajesNoLeidos || 0), 0);

  return (
    <div className="h-[calc(100vh-120px)] bg-background">
      <Card className="h-full overflow-hidden">
        <div className="flex h-full">
          {!isMobileView && (
            <>
              <div className="w-[350px] border-r h-full">
                <ChatSidebar 
                  conversaciones={conversaciones}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedConversacion={selectedConversacion}
                  setSelectedConversacion={setSelectedConversacion}
                  setShowNewChat={setShowNewChat}
                  totalNoLeidos={totalNoLeidos}
                  isMobileView={isMobileView}
                  setShowChatOnMobile={setShowChatOnMobile}
                />
              </div>
              <div className="flex-1 h-full">
                <ChatWindow 
                  selectedConversacion={selectedConversacion}
                  mensajes={mensajes}
                  llmEnabled={llmEnabled}
                  llmLoading={llmLoading}
                  nuevoMensaje={nuevoMensaje}
                  setNuevoMensaje={setNuevoMensaje}
                  handleSendMessage={handleSendMessage}
                  toggleLlmEnabled={toggleLlmEnabled}
                  loadingMore={loadingMore}
                  scrollContainerRef={scrollContainerRef}
                  messagesEndRef={messagesEndRef}
                  handleScroll={handleScroll}
                  isMobileView={isMobileView}
                  setShowChatOnMobile={setShowChatOnMobile}
                />
              </div>
            </>
          )}

          {isMobileView && (
            showChatOnMobile ? (
              <div className="w-full h-full">
                <ChatWindow 
                  selectedConversacion={selectedConversacion}
                  mensajes={mensajes}
                  llmEnabled={llmEnabled}
                  llmLoading={llmLoading}
                  nuevoMensaje={nuevoMensaje}
                  setNuevoMensaje={setNuevoMensaje}
                  handleSendMessage={handleSendMessage}
                  toggleLlmEnabled={toggleLlmEnabled}
                  loadingMore={loadingMore}
                  scrollContainerRef={scrollContainerRef}
                  messagesEndRef={messagesEndRef}
                  handleScroll={handleScroll}
                  isMobileView={isMobileView}
                  setShowChatOnMobile={setShowChatOnMobile}
                />
              </div>
            ) : (
              <div className="w-full h-full">
                <ChatSidebar 
                  conversaciones={conversaciones}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedConversacion={selectedConversacion}
                  setSelectedConversacion={setSelectedConversacion}
                  setShowNewChat={setShowNewChat}
                  totalNoLeidos={totalNoLeidos}
                  isMobileView={isMobileView}
                  setShowChatOnMobile={setShowChatOnMobile}
                />
              </div>
            )
          )}
        </div>
      </Card>

      <NewChatDialog 
        showNewChat={showNewChat}
        setShowNewChat={setShowNewChat}
        newChatData={newChatData}
        setNewChatData={setNewChatData}
        handleNewChat={handleNewChat}
      />
    </div>
  );
};

export default Mensajes;
